import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiGet, apiPost } from "../../shared/apiClient";
import { IconList, IconRefresh, IconSave } from "../../shared/toolbarIcons";

const emptyForm = () => ({
  name: "",
  productId: "",
  isActive: true
});

export function ProductTagsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const editParam = searchParams.get("edit");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadLookups = async () => {
    setLoading(true);
    setError(null);
    const pRes = await apiGet("/api/Products");
    if (pRes.ok) setProducts(pRes.data);
    else setError(pRes.error ?? "Failed to load products");
    setLoading(false);
  };

  useEffect(() => {
    void loadLookups();
  }, []);

  useEffect(() => {
    if (!editParam) {
      setEditingId(null);
      setForm(emptyForm());
      setLoading(false);
      return;
    }
    const id = Number(editParam);
    if (!Number.isFinite(id)) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      const res = await apiGet(`/api/ProductTags/${id}`);
      if (cancelled) return;
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product tag");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        name: x.name ?? "",
        productId: x.productId ?? "",
        isActive: Boolean(x.isActive)
      });
    })();
    return () => {
      cancelled = true;
      setLoading(false);
    };
  }, [editParam]);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm());
    setSearchParams({}, { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.productId) return;
    setSaving(true);
    setError(null);
    const res = await apiPost("/api/ProductTags", {
      id: editingId ?? 0,
      name: form.name || null,
      productId: Number(form.productId),
      isActive: form.isActive
    });
    if (!res.ok) setError(res.error ?? "Failed to save product tag");
    else {
      await loadLookups();
      resetForm();
    }
    setSaving(false);
  };

  const onRefresh = async () => {
    setError(null);
    await loadLookups();
    if (editParam) {
      const id = Number(editParam);
      if (!Number.isFinite(id)) return;
      setLoading(true);
      const res = await apiGet(`/api/ProductTags/${id}`);
      setLoading(false);
      if (!res.ok) {
        setError(res.error ?? "Failed to load product tag");
        return;
      }
      const x = res.data;
      setEditingId(x.id);
      setForm({
        name: x.name ?? "",
        productId: x.productId ?? "",
        isActive: Boolean(x.isActive)
      });
    } else {
      setEditingId(null);
      setForm(emptyForm());
    }
  };

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>{editingId ? "Edit Product Tag" : "Add Product Tag"}</h2>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="submit"
            form="product-tags-form"
            disabled={saving}
            title="Save"
            aria-label="Save product tag"
          >
            <IconSave />
          </button>
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            onClick={() => void onRefresh()}
            disabled={saving || loading}
            title="Refresh"
            aria-label="Refresh"
          >
            <IconRefresh />
          </button>
          <Link to="/product-tags-list" className="icon-button icon-button--toolbar" title="Product tags list" aria-label="Open list">
            <IconList />
          </Link>
        </div>
      </div>
      <form id="product-tags-form" onSubmit={onSubmit} className="card-body form form-fields-bottomline">
        <label className="field">
          <span>Product</span>
          <select value={form.productId} onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value ? Number(e.target.value) : "" }))}>
            <option value="">Select product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name || `Product #${p.id}`}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Tag Name</span>
          <input value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
        </label>
        <label className="field checkbox-field">
          <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
          <span>Is Active</span>
        </label>
        {error && <div className="error-banner">{error}</div>}
      </form>
    </div>
  );
}
